import { fetcher } from '@chipinside/fetcher'
import { Translate } from '@chipinside/translate'
import { Alert, AlertTitle, Button, LinearProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { resetFirebaseToken } from '#/store/ducks/auth'
import tryCache from '#/utils/tryCache'

const AUTO_RELOAD_SECONDS = 15
const CHECK_VERSION_INTERVAL_MS = 1000 * 60 * 5

export default function RevalidateVersion() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false)
  const [seconds, setSeconds] = useState(AUTO_RELOAD_SECONDS)
  const dispatch = useDispatch()

  const handleReload = (): void => {
    localStorage.removeItem('persist:root')
    tryCache.clearAll()
    dispatch(resetFirebaseToken())
    window.location.reload()
  }

  useEffect(() => {
    if (newVersionAvailable) return

    const checkVersion = (): void => {
      if (document.visibilityState !== 'visible') return
      fetcher<string>({ controller: `${window.location.origin}/version.txt?${Date.now()}` })
        .then(({ data: version }) =>
          setNewVersionAvailable(version !== import.meta.env.VITE_VERSION),
        )
        .catch(console.error)
    }

    checkVersion()
    const interval = setInterval(checkVersion, CHECK_VERSION_INTERVAL_MS)
    document.addEventListener('visibilitychange', checkVersion)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', checkVersion)
    }
  }, [newVersionAvailable])

  // A contagem fica pausada enquanto a aba está oculta
  useEffect(() => {
    if (!newVersionAvailable) return
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') setSeconds(current => current - 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [newVersionAvailable])

  useEffect(() => {
    if (seconds <= 0) handleReload()
  }, [seconds])

  if (!newVersionAvailable) return null

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        bottom: 0,
        maxWidth: '50pc',
        padding: '10px 50px',
        position: 'absolute',
        right: 0,
        zIndex: 5,
      }}
    >
      <AlertTitle>{Translate({ messageKey: 'update_available' })}</AlertTitle>
      <Typography>
        {Translate({ messageKey: 'outdated_version_warning', params: { seconds } })}
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 4, mt: 1 }}>
        <LinearProgress
          variant="determinate"
          value={100 - (seconds / AUTO_RELOAD_SECONDS) * 100}
          color="info"
          sx={{ borderRadius: 4, flex: 1, height: 5 }}
        />
        <Button size="small" variant="contained" color="info" sx={{ px: 3 }} onClick={handleReload}>
          {Translate({ messageKey: 'reload' })}
        </Button>
      </Stack>
    </Alert>
  )
}
